import { memo, ReactNode } from 'react'

import { Box, Typography } from '@mui/material'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { isEndNotAfterStart } from 'domains/Workspaces/Events/schedule'
import { ScheduleRangeFields } from 'domains/Workspaces/Events/ScheduleRangeFields'

import type { ConfigFormValues } from './types'

export type ScheduleFieldsProps = {
  /** Form actions (e.g. Cancel/Reset/Save) rendered inline in the "Schedule" header row. */
  actions?: ReactNode
}

/** Start/end schedule editor bound to `manualStartTime`/`manualEndTime` on the shared config form. */
const ScheduleFieldsRaw = ({ actions }: ScheduleFieldsProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const { control, getValues, trigger } = useFormContext<ConfigFormValues>()

  const startField = useController({ control, name: 'manualStartTime' }).field
  const endController = useController({
    control,
    name: 'manualEndTime',
    rules: {
      validate: (value) =>
        !isEndNotAfterStart(getValues('manualStartTime'), value) || t('domain:Workspace.Event.endBeforeStart'),
    },
  })

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="heading-md">{t('domain:Workspace.Event.schedule')}</Typography>
        {actions && <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>}
      </Box>
      <ScheduleRangeFields
        startTime={startField.value}
        endTime={endController.field.value}
        onStartTimeChange={(value) => {
          startField.onChange(value)
          // Re-validate the end field so the "before start" error clears/appears live.
          void trigger('manualEndTime')
        }}
        onEndTimeChange={endController.field.onChange}
        endError={endController.fieldState.error?.message}
      />
    </Box>
  )
}

export const ScheduleFields = memo(ScheduleFieldsRaw)
